import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@evaneos/b2b-ui';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import CloseIcon from '@evaneos/b2b-ui/icons/Close';
import FileDownloadOutlinedIcon from '@evaneos/b2b-ui/icons/FileDownloadOutlined';

import { useParams } from 'react-router-dom';

import { MessageAttachment } from '../../../../../resources/discussion/types';

import {
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../shared/analytics';

import { DossierId } from '../../../../../resources/dossier/types';

import { SharedByText } from './SharedByText';

type PhotoDetailModalProps = {
    attachment: MessageAttachment;
    messageInfo?: { authorName?: string; authorId?: string; sendDate?: Date };
    onClose: () => void;
    onDownloadLinkClick: () => void;
};

export const PhotoDetailModal = ({
    attachment,
    messageInfo,
    onClose,
    onDownloadLinkClick,
}: PhotoDetailModalProps) => {
    const { dossierId } = useParams() as { dossierId: DossierId };
    const { formatMessage } = useIntl();

    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    useEffect(() => {
        trackEvent((ampli, defaultProperties) => {
            ampli.attachmentPreviewDisplayed({
                ...defaultProperties,
                ...trackedTripProperties,
            });
        });
    }, [trackEvent, trackedTripProperties]);

    return (
        <Dialog open onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
            >
                <Box>
                    <Typography variant="h4" component="h2" mb={0.5}>
                        {attachment.name}
                    </Typography>
                    <SharedByText messageInfo={messageInfo} />
                </Box>
                <IconButton
                    aria-label={formatMessage({ id: 'global.close' })}
                    onClick={onClose}
                    sx={{ ml: 2, flexShrink: 0 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    backgroundColor: 'background.tertiary',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    p: 0,
                    minHeight: 300,
                }}
            >
                <Box
                    component="img"
                    src={attachment.link}
                    alt={attachment.name}
                    sx={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                    }}
                />
            </DialogContent>

            <DialogActions>
                <Button
                    component="a"
                    href={attachment.link}
                    variant="outlined"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={onDownloadLinkClick}
                >
                    {formatMessage({ id: 'discussion.attachments_list.download_button' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
